/// <amd-module name="DistributedTaskUI/SecurityComponent/UserPermissionsAuthorization/Components/RoleAssignmentComponentSecurityControl" />
import * as React from "react";
import { Table, ITableColumn, SimpleTableCell } from "VSSUI/Table";
import RoleAssignmentComponentSecurityStore = require("UserPermissionsAuthorization/Store/RoleAssignmentComponentSecurityStore");
import RoleAssignmentComponentSecuritySource = require("UserPermissionsAuthorization/Source/RoleAssignmentComponentSecuritySource");
import { AddUserDialog } from "UserPermissionsAuthorization/Components/AddUserDialog";
import * as Resources from "DistributedTaskUI/SecurityComponent/Resources";
import Roles_Contracts = require("SecurityRoles/Client/Contracts/SecurityRoles");
import { Card } from "VSSUI/Card";
import { Button } from "VSSUI/Button";
import { ButtonGroup } from "VSSUI/ButtonGroup";
import { Dropdown } from "VSSUI/Dropdown";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";
import { IListBoxItem } from "VSSUI/ListBox";
import { IMenuItem, MenuButton } from "VSSUI/Menu";
import { Tab, TabBar, TabSize } from "VSSUI/Tabs";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";
import RoleAssignmentComponentSecurityActionsCreator = require("UserPermissionsAuthorization/Actions/RoleAssignmentComponentSecurityActionsCreator");
import RoleAssignmentComponentSecurityActionsHub = require("UserPermissionsAuthorization/Actions/RoleAssignmentComponentSecurityActionsHub");
import { ObservableValue } from "VSS/Core/Observable";
import { VssComponent, IVssComponentContext } from "VSS/Platform/Layout";
import { getIdentityImageUrl } from "VSS/Platform/Identity";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { SecurityNamespaceConstants } from "DistributedTaskUI/SecurityComponent/Constants";
import { Pill, PillSize } from "VSSUI/Pill";

export interface IGridSizeSetting {
    userCellWidth: number;
    roleCellWidth: number;
    accessCellWidth: number;
    removeCellWidth: number;
}

let GridSizeConstants: IGridSizeSetting = {
    userCellWidth: 250,
    roleCellWidth: 100,
    accessCellWidth: 100,
    removeCellWidth: 45
};

export interface IRoleAssignmentSecurityProps {
    /**
     * Instance Id of the RoleAssignment host
     */
    serviceInstanceId: string | undefined;
    /**
     * Id of the user opening the control
     */
    userId: string;
    /**
    * Title appearing at the top of the control
    */
    title?: string;
    /**
     * Id of the resource on which roles are being managed
     */
    resourceId: string;
    /**
     * Scope in which roles are being managed
     */
    scopeId: string;
    /**
     * Specifies the permissions required to assign roles. 
     * If none is provided, all users will be able to edit the control.
     */
    manageRolesPermission?: number;
    /**
     * Message to be displayed if the user cannot make changes to role assignments
     */
    noPermissionMessage?: string;
    /**
     * Specifies directly whether current user can assign roles or not.
     */
    canEditProjectView?: boolean;
    /**
     * Specifies directly whether current user can share projects or not.
     */
    canEditOrganizationView?: boolean;
    /**
     * If true, hides descriptions of selected roles in the add user dialog
     */
    hideRoleDescriptions?: boolean;
    /**
     * If true, displays users' avatars in the role assignments grid
     */
    showAvatars?: boolean;
    /**
     * To override the text shown at the "Users and Groups" label in Add users dialog box
     */
    addUserDialogUserLabel?: string;

    noPermissionsMessage?: string;

    /**
     * To display the inheritance toggle control to the users.
     */
    enableToggleInheritence?: boolean;

    endpointId: string;

    className?: string;

    identityTypes: string[];

    operationScopes: string[];

    roleScope: string;

    enableTogglingOrganizationButton: boolean;
}

export class RoleAssignmentComponentSecurityControl extends VssComponent<IRoleAssignmentSecurityProps, RoleAssignmentComponentSecurityStore.IRoleAssignmentSecurityState>{

    public static componentType = "vssRoleAssignmentComponentSecurityControl";
    private _actionsCreator: RoleAssignmentComponentSecurityActionsCreator.RoleAssignmentComponentSecurityActionsCreator;
    private _store: RoleAssignmentComponentSecurityStore.RoleAssignmentComponentSecurityStore;
    private _actions: RoleAssignmentComponentSecurityActionsHub.RoleAssignmentComponentSecurityActionsHub;
    private _source: RoleAssignmentComponentSecuritySource.RoleAssignmentComponentSecuritySource;
    private _performanceService: IVssPerformanceService;
    private _roleAssignmentComponentFeature: string = "RoleAssignmentComponent";
    private _roleAssignmentComponentScenario: string = "RoleAssignmentComponent.Loaded";
    private _loggedPerfEvent: boolean = false;
    private _dropdownSelectionDictionary: { [key: string]: DropdownSelection } = {};

    constructor(props: IRoleAssignmentSecurityProps, context: IVssComponentContext) {
        super(props, context);
        this.context = context;
        this._performanceService = this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        this._performanceService.startScenario(this._roleAssignmentComponentScenario, true);
        this._actions = new RoleAssignmentComponentSecurityActionsHub.RoleAssignmentComponentSecurityActionsHub();
        this._source = new RoleAssignmentComponentSecuritySource.RoleAssignmentComponentSecuritySource(this.context, this.props.serviceInstanceId);
        this._actionsCreator = new RoleAssignmentComponentSecurityActionsCreator.RoleAssignmentComponentSecurityActionsCreator(this._actions,
            this.props.resourceId, this.props.scopeId, this.props.endpointId, this._source);
        this._actionsCreator.initialize();
        let roleAssignmentComponentProps: RoleAssignmentComponentSecurityStore.IRoleAssignmentSecurityStoreProps = {
            userId: this.props.userId,
            canEditProjectView: this.props.canEditProjectView,
            manageRolesPermission: this.props.manageRolesPermission,
            roleScope: this.props.roleScope,
            canEditOrganizationView: this.props.canEditOrganizationView
        };
        this.selectedTabId = new ObservableValue(Resources.ProjectButtonText);
        this._store = new RoleAssignmentComponentSecurityStore.RoleAssignmentComponentSecurityStore(this._actions, roleAssignmentComponentProps);
    }

    public componentWillMount(): void {
        this._store.addChangedListener(this._onStoreChange);

        let state = this._store.getState();
        this.setState(state);
    }

    public componentWillUnmount(): void {
        this._store.removeChangedListener(this._onStoreChange);
    }

    public componentDidMount(): void {
        let securityPageBackButton: HTMLElement = document.getElementById("__bolt-security-page-back-button-id");
        if (securityPageBackButton) {
            securityPageBackButton.focus();
        }
    }

    public componentDidUpdate(prevProps: any, prevState: any): void {
        if (this.state !== prevState && !this._loggedPerfEvent) {
            this._performanceService.endScenario(this._roleAssignmentComponentFeature, this._roleAssignmentComponentScenario, true);
            this._loggedPerfEvent = true;
        }
    }

    public render(): JSX.Element {
        let gridContainerClassName = this.state.canEdit ? "grid-container" : "grid-container no-edit";
        let itemsProvider = new ArrayItemProvider<RoleAssignmentComponentSecurityStore.IRoleAssignmentSecurityGridRow>(this.state.roleAssignments);
        let menuItems: IMenuItem[] = [
            {
                id: "Inheritance-On",
                text: Resources.InheritenceOnButtonText,
                checked: this.state.inherit,
                onActivate: () => {
                    this._changeInheritance(true);
                },
                readonly: true
            },
            {
                id: "Inheritance-Off",
                text: Resources.InheritenceOffButtonText,
                checked: !this.state.inherit,
                onActivate: () => {
                    this._changeInheritance(false);
                },
                readonly: true
            }
        ];
        let projectMenuItems: IMenuItem[] = [
            {
                id: "roles-project",
                text: Resources.ProjectButtonText,
                iconProps: {
                    iconName: this.state.roleScope === Resources.ProjectButtonText ? "CheckMark" : ""
                },
                onActivate: () => {
                    this._changeScope(Resources.ProjectButtonText);
                }
            },
            {
                id: "roles-organization",
                text: Resources.OrganizationButtonText,
                iconProps: {
                    iconName: this.state.roleScope === Resources.OrganizationButtonText ? "CheckMark" : ""
                },
                onActivate: () => {
                    this._changeScope(Resources.OrganizationButtonText);
                }
            }
        ];
        let classNameForUndoIcon = "itemIconColor";
        let classNameForAddIcon = "itemIconColor";
        if (this.state.canEdit) {
            classNameForAddIcon = "itemIconColor-add";
        }
        if (this.state.canEdit && this.state.isDirty) {
            classNameForUndoIcon = "itemIconColor-undo";
        }
        let enableToggleInheritence = true;
        if (typeof this.props.enableToggleInheritence === "boolean") {
            // If value specified directly, use it
            enableToggleInheritence = this.props.enableToggleInheritence;
        }
        let className = "roles-permissions-card bolt-card-no-vertical-padding flex-column";
        return (
            <div className="flex flex-column">
                {
                    !this.props.canEditOrganizationView &&
                    this._getNoManagePermissionCardComponent()
                }
                {
                    this.state.error && this._getErrorCardComponent()
                }
                
                <Card className={className}
                    contentProps={{ contentPadding: false, className: "user-permissions-card-content" }}>
                    {
                        this.props.title &&
                        <h2 className="roles-tab-title">
                            {this.props.title}
                        </h2>
                    }
                    {
                        this.props.enableTogglingOrganizationButton &&
                        <ButtonGroup className="projectCommandBar">
                            <div className="flex-column">
                                <TabBar
                                    tabsClassName="bolt-card-white"
                                    onSelectedTabChanged={this.onSelectedTabChanged}
                                    selectedTabId={this.selectedTabId}
                                    tabSize={TabSize.Tall}
                                >
                                    <Tab name={Resources.ProjectButtonText} id={Resources.ProjectButtonText} />
                                    <Tab name={Resources.OrganizationButtonText} id={Resources.OrganizationButtonText} />
                                </TabBar>
                            </div>
                        </ButtonGroup>
                    }
                    <ButtonGroup className="commandBar">
                        <Button className="commandBar-Item"
                            text={Resources.AddButtonText}
                            iconProps={{ iconName: "Add", className: classNameForAddIcon }}
                            disabled={!this.state.canEdit}
                            subtle={true}
                            onClick={
                                () => {
                                    this.setState({
                                        showAddIdentityPicker: true
                                    });
                                }
                            }
                        />
                        <Button className="commandBar-Item"
                            text={Resources.UndoButtonText}
                            iconProps={{ iconName: "Undo", className: classNameForUndoIcon }}
                            disabled={!this.state.canEdit || !this.state.isDirty}
                            subtle={true}
                            onClick={
                                () => {
                                    this._actionsCreator.initialize();
                                }
                            }
                        />
                        <Button className="commandBar-Item"
                            text={Resources.SaveButtonText}
                            iconProps={{ iconName: "Save", className: classNameForUndoIcon }}
                            disabled={!this.state.canEdit || !this.state.isDirty}
                            subtle={true}
                            onClick={
                                () => {
                                    this._saveChanges();
                                }

                            }
                        />
                        {this.state.roleScope === Resources.ProjectButtonText &&
                            this.props.enableToggleInheritence && <MenuButton className="commandBar-Item"
                                ariaLabel={this.state.inherit ? Resources.InheritenceOnButtonSelectText : Resources.InheritenceOffButtonSelectText}
                                text={Resources.InheritanceButtonText}
                                disabled={!this.state.canEdit}
                                subtle={true}
                                contextualMenuProps={{
                                    menuProps: {
                                        id: "inheritence",
                                        items: menuItems
                                    }
                                }}
                            />}
                    </ButtonGroup>
                    <div className={gridContainerClassName}>
                        <Table ariaLabel={Resources.UserPermissionsTableName} className="roles-grid" scrollable={true} columns={this._tableColumns} itemProvider={itemsProvider} pageSize={20} />
                    </div>

                    {this.state.showAddIdentityPicker &&
                        <AddUserDialog
                            title={Resources.AddUsersDialogTitle}
                            roles={this.state.roles}
                            defaultRole={this.state.roleScope === Resources.OrganizationButtonText ? Resources.AdministratorRoleText : null}
                            addCallback={this._actionsCreator.tryAddUser}
                            addUserDialogUserLabel={this.props.addUserDialogUserLabel}
                            hideRoleDescriptions={this.props.hideRoleDescriptions}
                            onCancel={() => {
                                this.setState({
                                    showAddIdentityPicker: false
                                });
                            }}
                            identityTypes={this.props.identityTypes}
                            operationScopes={this.props.operationScopes}
                        />
                    }
                </Card>
            </div >
        );
    }

    private onSelectedTabChanged = (newTabId: string) => {
        this.selectedTabId.value = newTabId;
        this._changeScope(newTabId);
    }

    private _onSizeSizable = (event: MouseEvent, index: number, width: number) => {
        (this._tableColumns[index].width as ObservableValue<number>).value = width;
    }
    private _tableColumns: ITableColumn<RoleAssignmentComponentSecurityStore.IRoleAssignmentSecurityGridRow>[] = [
        {
            id: "user",
            minWidth: GridSizeConstants.userCellWidth,
            width: new ObservableValue(600),
            name: Resources.UserPermissionUserHeading,
            renderCell: this._renderUserCellContent.bind(this),
            onSize: this._onSizeSizable,
            ariaLabel: Resources.UserPermissionUserHeading
        },
        {
            id: "role",
            minWidth: GridSizeConstants.roleCellWidth,
            width: new ObservableValue(250),
            name: Resources.UserPermissionRoleHeading,
            renderCell: this._renderRoleCellContent.bind(this),
            onSize: this._onSizeSizable,
            ariaLabel: Resources.UserPermissionRoleHeading
        },
        {
            id: "access",
            minWidth: GridSizeConstants.accessCellWidth,
            width: new ObservableValue(300),
            name: Resources.UserPermissionAccessHeading,
            renderCell: this._renderAccessCellContent.bind(this),
            onSize: this._onSizeSizable,
            ariaLabel: Resources.UserPermissionAccessHeading
        }
    ];

    private _renderUserCellContent(rowIndex: number, columnIndex: number, tableColumn: ITableColumn<RoleAssignmentComponentSecurityStore.IRoleAssignmentSecurityGridRow>, dataItem: RoleAssignmentComponentSecurityStore.IRoleAssignmentSecurityGridRow) {
        const title = dataItem.role.identity.uniqueName ? dataItem.role.identity.uniqueName : dataItem.displayName;
        const res = getIdentityImageUrl(this.context.pageContext, dataItem.role.identity);
        let rowClassName = "role-user-cell", userPicClassName = "user-pic", avatarClassName = "scroll-hidden role-name-cell avatar", roleNameClassName = "scroll-hidden role-name-cell";
        if (dataItem.isRemoved) {
            userPicClassName = userPicClassName + " removed";
            avatarClassName = avatarClassName + " removed";
            roleNameClassName = roleNameClassName + " removed";
            rowClassName = rowClassName + " removed";
        }
        if (dataItem.isDirty) {
            userPicClassName = userPicClassName + " dirty";
            avatarClassName = avatarClassName + " dirty";
            roleNameClassName = roleNameClassName + " dirty";
            rowClassName = rowClassName + " dirty";
        }
        return (
            <SimpleTableCell ariaLabel={Resources.UserPermissionUserHeading + " " + dataItem.displayName} className={rowClassName} columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
                <div className={rowClassName}>
                    {
                        this.props.showAvatars &&

                        <img className={userPicClassName} src={res} alt="user pic" />

                    }
                    {
                        this.props.showAvatars &&
                        <div className={avatarClassName} title={title}>
                            {dataItem.displayName}
                        </div>
                    }
                    {
                        !this.props.showAvatars &&
                        <div className={roleNameClassName} title={title}>
                             {dataItem.displayName}
                        </div>
                    }
                </div>
            </SimpleTableCell>
        );
    }

    private _renderRoleCellContent(rowIndex: number, columnIndex: number, tableColumn: ITableColumn<RoleAssignmentComponentSecurityStore.IRoleAssignmentSecurityGridRow>, dataItem: RoleAssignmentComponentSecurityStore.IRoleAssignmentSecurityGridRow) {
        let rowClassName = "role-menu-cell";
        if (dataItem.isRemoved) {
            rowClassName = rowClassName + " removed";
        }
        if (dataItem.isDirty) {
            rowClassName = rowClassName + " dirty";
        }
        let options: IListBoxItem[] = [];
        let label = dataItem.role.identity.id;
        if (!this._dropdownSelectionDictionary[label]) {
            this._dropdownSelectionDictionary[label] = new DropdownSelection();
        }
        if (this.state.roles) {
            this.state.roles.map((item: Roles_Contracts.SecurityRole, index) => {
                options.push({
                    id: item.displayName,
                    text: item.displayName
                });
                if (item.displayName === dataItem.roleName) {
                    this._dropdownSelectionDictionary[label].select(index);
                }
            });
        }

        if ((!this._store.getUserRoleAssignment() || dataItem.role.identity.id !== this._store.getUserRoleAssignment().identity.id)
            && this.state.canEdit
            && dataItem.isRemoved !== true) {
            return (
                <SimpleTableCell className={rowClassName} columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
                    <Dropdown
                        ariaLabel={Resources.UserPermissionRoleHeading + " " + dataItem.roleName}
                        disabled={!this.state.canEdit}
                        className="propagate-keydown-event"
                        selection={this._dropdownSelectionDictionary[label]}
                        items={options}
                        onSelect={(event: React.SyntheticEvent<HTMLElement>, selection?: IListBoxItem) => {
                            if (selection) {
                                this._onRoleChanged(dataItem, selection.text);
                            }
                        }}
                    />
                </SimpleTableCell>);
        }
        else {
            this._dropdownSelectionDictionary[label].select(0);
            options = [];
            options.push({
                id: dataItem.roleName,
                text: dataItem.roleName
            });
            return (
                <SimpleTableCell className={rowClassName} columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
                    <Dropdown
                        ariaLabel={Resources.UserPermissionRoleHeading + " " + dataItem.roleName}
                        disabled={!this.state.canEdit}
                        className="propagate-keydown-event"
                        selection={this._dropdownSelectionDictionary[label]}
                        items={options}
                        onSelect={(event: React.SyntheticEvent<HTMLElement>, selection?: IListBoxItem) => {
                            if (selection) {
                                this._onRoleChanged(dataItem, selection.text);
                            }
                        }
                        }
                    />
                </SimpleTableCell>);
        }

    }

    private _renderAccessCellContent(rowIndex: number, columnIndex: number, tableColumn: ITableColumn<RoleAssignmentComponentSecurityStore.IRoleAssignmentSecurityGridRow>, dataItem: RoleAssignmentComponentSecurityStore.IRoleAssignmentSecurityGridRow): JSX.Element {
        let removePermission = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
            this._onRemove(dataItem);
        };
        let rowClassName = "role-access-cell";
        if (dataItem.isRemoved) {
            rowClassName = rowClassName + " removed";
        }
        if (dataItem.isDirty) {
            rowClassName = rowClassName + " dirty";
        }
        return (
            <SimpleTableCell ariaLabel={Resources.UserPermissionAccessHeading + " " + dataItem.role.accessDisplayName} className={rowClassName} columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
                <div>
                    {/* className={rowClassName}> */}
                    {dataItem.role.accessDisplayName}
                </div>
                {
                    // Do not show remove button if the identity is himself or role is inherited
                    (this.state.canEdit
                        && dataItem.isRemoved !== true
                        && dataItem.role.identity.id !== this.props.userId
                        && dataItem.accessValue !== Roles_Contracts.RoleAccess.Inherited) &&
                    <Button tooltipProps={{ text: Resources.RevokeAccessTooltip }} primary={false} className="bolt-table-cell-content-reveal user-removal-button" subtle={true} iconProps={{ iconName: "Cancel", className: "removal-icon" }} ariaLabel={Resources.Removepermission} onClick={removePermission} excludeTabStop={true} />
                }
            </SimpleTableCell>
        );
    }

    private _onStoreChange = (): void => {
        let state = this._store.getState();
        this.setState(state);
    }

    private _changeInheritance(enableInheritance: boolean): void {
        if (this.state.inherit !== enableInheritance) {
            this._actionsCreator.changeInheritance(enableInheritance);
        }
    }

    private _changeScope(scope: string): void {
        if (scope === Resources.OrganizationButtonText) {
            this._actionsCreator.changeScope("collection_" + this.props.endpointId, scope, SecurityNamespaceConstants.CollectionScopeId);
        }
        else {
            this._actionsCreator.changeScope(this.props.resourceId, scope, this.props.scopeId);
        }
    }

    private _saveChanges = (): void => {
        let dirtyRows = this._store.getDirtyRows();
        dirtyRows = dirtyRows.filter(row => !this._store.userIsRemoved(row.role.identity.id));
        let updatedRoleAssignments = this._makeRolesToAssign(dirtyRows);
        let removedUserIds = this._store.getRemovedUserIds();

        this._actionsCreator.beginSave(updatedRoleAssignments, removedUserIds);
    }

    private _makeRolesToAssign(dirtyRows: RoleAssignmentComponentSecurityStore.IRoleAssignmentSecurityGridRow[]): Roles_Contracts.UserRoleAssignmentRef[] {
        let userRefs = [];
        for (let i = 0; i < dirtyRows.length; i++) {
            let row = dirtyRows[i];
            userRefs.push({
                userId: row.role.identity.id,
                roleName: row.roleName,
                uniqueName: row.role.accessDisplayName
            });
        }
        return userRefs;
    }

    private _onRoleChanged = (dataItem: RoleAssignmentComponentSecurityStore.IRoleAssignmentSecurityGridRow, selectedValue: String): void => {
        let items = selectedValue;
        let role1 = this.state.roles.filter(role => { return role.name === items; });
        let updatedRoleAssignment = {
            access: dataItem.role.access,
            accessDisplayName: dataItem.role.accessDisplayName,
            identity: dataItem.role.identity,
            role: role1[0]
        };

        this._actionsCreator._actions.updateRoleAssignment.invoke(updatedRoleAssignment);
    }

    private _onRemove(dataItem: RoleAssignmentComponentSecurityStore.IRoleAssignmentSecurityGridRow): void {
        this._actionsCreator._actions.removeUser.invoke(dataItem.role.identity.id);
    }

    private _getNoManagePermissionCardComponent(): JSX.Element {
        let errorMessage = "";
        if (!this.state.canEdit) {
            errorMessage = Resources.CannotUpdateSecurityMessage;
        }
        else {
            if (this.state.roleScope === Resources.OrganizationButtonText && !this.props.canEditOrganizationView) {
                errorMessage = Resources.CannotUpdateOrganizationView;
            }
        }
        if (errorMessage !== "") {
            return (
                <MessageCard className="cant-manage-message-card" severity={MessageCardSeverity.Warning}>
                    {this.props.noPermissionMessage ? this.props.noPermissionMessage : errorMessage}
                </MessageCard>
            );
        }
        return null;
    }

    private _getErrorCardComponent(): JSX.Element {
        return (
            <div>
                <MessageCard className="error-message-card" onDismiss={() => { this._actionsCreator.setErrorMessage(""); }} severity={MessageCardSeverity.Error}>
                    {this.state.error}
                </MessageCard>
            </div>
        );
    }

    private selectedTabId : ObservableValue<string>;
}